/*
 * Street and house number splitting (ERS-5). The BNPL checkout design has one "Straße + Nr."
 * field, but the provider's `RbiCoursePaymentBillingCustomer` and Firestore's
 * `webAppOnboarding.privateInfo.address` both want `street` + `houseNumber` separately.
 *
 * Ported from `physiofit_cloud_functions/functions/utils/patientMasterData/streetAddress.ts`
 * (PF-4935) — there is no shared client util today. The load-bearing property is NOT that the
 * heuristic guesses correctly, but that it is reversible up to whitespace:
 *   joinStreet(splitStreet(x)) === x.trim().replace(/\s+/g, ' ')
 * A wrong guess is therefore invisible in rendering, since the joined address stays correct.
 */

export type SplitStreet = { street: string; houseNumber: string };

export const splitStreet = (input?: string): SplitStreet => {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return { street: '', houseNumber: '' };

  const tokens = trimmed.split(/\s+/);
  let splitIndex = -1;
  tokens.forEach((token, index) => {
    if (/\d/.test(token)) splitIndex = index;
  });

  // No match, or the match is the first token: do not split, or the street name would end up in
  // the house number and `street` would be empty.
  if (splitIndex <= 0) return { street: trimmed, houseNumber: '' };

  return {
    street: tokens.slice(0, splitIndex).join(' '),
    // Everything FROM the match on, not just the token itself: "Berliner Allee 3 / Hinterhaus"
    // would otherwise lose its suffix.
    houseNumber: tokens.slice(splitIndex).join(' '),
  };
};

export const joinStreet = (parts?: { street?: string; houseNumber?: string }): string =>
  [parts?.street, parts?.houseNumber].filter(Boolean).join(' ').trim();
